import React from 'react'
import { NodeViewWrapper } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { IconComponent } from '@/components/icon-component'
import { useAtom } from 'jotai'
import { activeBranchAtom, workspaceIdAtom, activeWorkspaceWithStorageAtom } from '@/stores/workspaceStore'
import { ensureInternalDocParams, isInternalDocLink } from '@/utils/link-helpers'

interface InlineButtonNodeViewProps {
  node: {
    attrs: {
      text?: string
      href?: string
      variant?: 'primary' | 'secondary'
      icon?: string
    }
  }
  deleteNode: () => void
  selected: boolean
}

export function InlineButtonNodeView({ node, selected }: InlineButtonNodeViewProps) {
  const { text = 'Button', href = '', variant = 'primary', icon = '' } = node.attrs
  const [workspaceId] = useAtom(workspaceIdAtom)
  const [activeBranch] = useAtom(activeBranchAtom)
  const [workspace] = useAtom(activeWorkspaceWithStorageAtom)

  // Parse icon data
  let iconElement = null
  if (icon) {
    let iconValue = icon
    let iconCategory: 'EMOJI' | 'LUCIDE' = 'LUCIDE'

    if (icon.startsWith('emoji:')) {
      iconValue = icon.replace('emoji:', '')
      iconCategory = 'EMOJI'
    }

    iconElement = (
      <span className="inline-button-icon">
        <IconComponent
          icon={iconValue}
          iconCategory={iconCategory}
          iconType="REGULAR"
          iconColor={variant === 'primary' ? '#fff' : '#000'}
          size={16}
        />
      </span>
    )
  }

  const handleClick = (e: React.MouseEvent) => {
    if (href) {
      e.preventDefault()
      e.stopPropagation()

      const go = async () => {
        // Save current document before navigation
        try {
          const saver = (window as any).tiptapForceSaveDocument
          if (typeof saver === 'function') {
            await saver()
          }
        } catch (_e) {}

        // Handle internal links with parameter completion
        if (isInternalDocLink(href)) {
          const finalHref = workspaceId
            ? await ensureInternalDocParams(href, workspaceId, activeBranch?.id, workspace?.slug)
            : href

          if (process.env.NODE_ENV === 'development') {
            console.log('[InlineButton] Navigate to:', finalHref, {
              originalHref: href
            })
          }

          window.location.href = finalHref
        } else {
          // External links
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      }
      void go()
    }
  }
  
  return (
    <NodeViewWrapper 
      as="span"
      className="inline"
    >
      <button
        className={cn(
          'inline-button',
          `inline-button--${variant}`,
          selected && 'inline-button--selected'
        )}
        data-inline-button=""
        data-href={href}
        data-text={text}
        data-variant={variant}
        data-icon={icon}
        onClick={handleClick}
        contentEditable={false}
      >
        {iconElement}
        {text}
      </button>
    </NodeViewWrapper>
  )
}